import type { RentalAgencyPin } from './rental_map_pins.js';

/**
 * O AGRUPADOR dos pinos do carro (#3995), sem DOM: pinos que colidem na tela
 * viram uma pílula "{n} agências · R$ {menor}+". No carro várias locadoras
 * ficam lado a lado (os balcões do aeroporto) — esconder o pino que colide,
 * como o declutter do hotel, sumiria com agência.
 *
 * A distância é a da TELA no zoom dado, pela projeção de Mercator da Mapbox
 * (mundo de 512px no zoom 0): entre dois pontos ela depende só do zoom, não de
 * para onde a câmera olha — então o agrupamento muda no `zoomend`, não a cada
 * arraste, e a conta é pura (testável sem o mapa).
 */

/** O raio de colisão em px de tela: duas pílulas mais perto que isto se
 *  sobrepõem. */
const CLUSTER_RADIUS_PX = 40;

/** A partir deste zoom os pinos não agrupam mais (o `clusterMaxZoom` da
 *  Mapbox): o toque no agrupador aproxima no máximo até aqui, onde todo pino
 *  aparece sozinho. Agências na MESMA coordenada já são um pino só
 *  (`rentalAgencyPins`), e o toque nele filtra a lista por aquele ponto. */
export const CLUSTER_MAX_ZOOM = 17;

const WORLD_PX = 512;

/** O ponto em px de mundo no zoom dado (Web Mercator, o da Mapbox). */
export function worldPoint(
  { latitude, longitude }: RentalAgencyPin['coordinates'],
  zoom: number,
): { x: number; y: number } {
  const scale = WORLD_PX * 2 ** zoom;
  const sin = Math.sin((latitude * Math.PI) / 180);
  return {
    x: ((longitude + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sin) / (1 - sin)) / (4 * Math.PI)) * scale,
  };
}

/** Um item do mapa: um pino sozinho ou o agrupador de vários. */
export type RentalMapCluster = {
  /** As chaves dos pinos, em ordem: estável enquanto o agrupamento não muda. */
  key: string;
  pins: readonly RentalAgencyPin[];
  /** O menor total entre os pinos. Ausente quando nenhum tem preço. */
  fromPrice?: number;
};

function clusterOf(pins: readonly RentalAgencyPin[]): RentalMapCluster {
  const prices = pins.flatMap((pin) => (pin.fromPrice === undefined ? [] : [pin.fromPrice]));
  return {
    key: pins.map((pin) => pin.key).join('|'),
    pins,
    ...(prices.length > 0 ? { fromPrice: Math.min(...prices) } : {}),
  };
}

/**
 * Agrupa os pinos que colidem no zoom dado. Guloso e estável: na ordem da
 * lista, cada pino entra no primeiro agrupador cuja SEMENTE está a menos de
 * `CLUSTER_RADIUS_PX`, senão vira semente — o agrupador nasce onde está o pino
 * que a lista mostra primeiro. No `CLUSTER_MAX_ZOOM` ou acima, cada pino fica
 * sozinho.
 */
export function clusterRentalPins(
  pins: readonly RentalAgencyPin[],
  zoom: number,
): RentalMapCluster[] {
  if (zoom >= CLUSTER_MAX_ZOOM) return pins.map((pin) => clusterOf([pin]));
  const groups: { seed: { x: number; y: number }; pins: RentalAgencyPin[] }[] = [];
  for (const pin of pins) {
    const point = worldPoint(pin.coordinates, zoom);
    const group = groups.find(
      ({ seed }) => Math.hypot(seed.x - point.x, seed.y - point.y) < CLUSTER_RADIUS_PX,
    );
    if (group) group.pins.push(pin);
    else groups.push({ seed: point, pins: [pin] });
  }
  return groups.map((group) => clusterOf(group.pins));
}
